import './CopAssetsTreePopup.scss'

import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import Loader from '../../common/components/Loader'
import { ChevronIcon } from '../copSiteDetails/component/SiteDetailsIcons'
import { SiteAreaContainer, SiteAsset } from '../copSiteDetails/services/copSiteDetails.service'
import PopupContainer from '../Shared/PopupContainer/PopupContainer'
import AssetsTreeHeader from './AssetsTreeHeader'
import { useAssetsTree } from './hooks/useAssetsTree'

function AssetCol({ children }: Readonly<{ children: ReactNode }>) {
  const ref = useRef<HTMLSpanElement | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const text = typeof children === 'string' || typeof children === 'number'
    ? String(children)
    : ''

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth)
    check()
  }, [children])

  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      setIsTruncated(el.scrollWidth > el.clientWidth)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <span
      ref={ref}
      className="assets-tree-popup__asset-col"
      title={isTruncated && text ? text : undefined}
    >
      {children}
    </span>
  )
}

type AssetsTreePopupProps = {
  isOpen: boolean
  siteId: string
  onClose: () => void
  allAssetsLabel?: string
  spareAssetsLabel?: string
  decommissionedAssetsLabel?: string
  errorMessageLabel?: string
  expandAllLabel?: string
  collapseAllLabel?: string
  viewLabel?: string
  allAssetsSiteLabel?: string
  allAssetsSiteLabelValue?: string
  spareAssetsItemLabel?: string
  spareAssetsItemLabelValue?: string
  decommissionedAssetsItemLabel?: string
  decommissionedAssetsItemLabelValue?: string
  viewPagePath?: string
  viewButtonLinksJson?: string
  noAssetMessage?: string
}

export default function CopAssetsTreePopup({
  isOpen, siteId, onClose,
  allAssetsLabel, spareAssetsLabel, decommissionedAssetsLabel, errorMessageLabel,
  expandAllLabel, collapseAllLabel, viewLabel,
  allAssetsSiteLabel, allAssetsSiteLabelValue,
  spareAssetsItemLabel, spareAssetsItemLabelValue,
  decommissionedAssetsItemLabel: _decommissionedAssetsItemLabel, decommissionedAssetsItemLabelValue,
  viewPagePath, viewButtonLinksJson, noAssetMessage,
}: Readonly<AssetsTreePopupProps>) {

  const {
    treeData, hasApiError, isLoading,
    selectedFilter, isDropdownOpen, setIsDropdownOpen,
    isSiteExpanded, setIsSiteExpanded,
    expandedAreas, expandedAssets,
    isSpareGroupExpanded, setIsSpareGroupExpanded,
    isDecomGroupExpanded, setIsDecomGroupExpanded,
    handleFilterSelect, toggleArea, toggleAsset,
    handleExpandAll, handleCollapseAll,
  } = useAssetsTree(isOpen, siteId, errorMessageLabel)

  // Build productCategoryName → viewContentPath lookup from authored JSON
  const viewLinkMap = useMemo(() => {
    try {
      const links: { productCategoryName: string; viewContentPath: string }[] = JSON.parse(viewButtonLinksJson ?? '[]')
      return new Map(links.map(l => [l.productCategoryName, l.viewContentPath]))
    } catch {
      return new Map<string, string>()
    }
  }, [viewButtonLinksJson])

  const getAssetViewUrl = (asset: SiteAsset): string => {
    const query = `?product-category-id=${asset.productCategoryId}`
    const categoryPath = viewLinkMap.get(asset.productCategoryName)
    if (categoryPath) return `${categoryPath}/${asset.assetId}${query}`
    if (viewPagePath) return `${viewPagePath}/${asset.assetId}${query}`
    return `/sites/${asset.siteId}/assets/${asset.assetId}${query}`
  }

  const renderAssetRow = (asset: SiteAsset, depth = 0): ReactNode => {
    const hasChildren = (asset.assets ?? []).length > 0
    const isExpanded = expandedAssets.has(asset.assetId)
    return (
      <div key={asset.assetId} className="assets-tree-popup__asset-node" style={{ paddingLeft: `${depth * 20}px` }}>
        <div className="assets-tree-popup__asset-row">
          <span className="assets-tree-popup__asset-chevron">
            {hasChildren
              ? (
                <button
                  type="button"
                  className="assets-tree-popup__asset-expand-btn"
                  onClick={() => toggleAsset(asset.assetId)}
                >
                  <ChevronIcon open={isExpanded} />
                </button>
              )
              : <span className="assets-tree-popup__asset-expand-placeholder" />}
          </span>
          <span className="assets-tree-popup__asset-col">{asset.serialNumber ?? '-'}</span>
          <AssetCol>{asset.description}</AssetCol>
          <AssetCol>{asset.operationalStatus}</AssetCol>
          <AssetCol>{asset.productCategoryName}</AssetCol>
          <div className="assets-tree-popup__asset-right">
            <span className="assets-tree-popup__asset-type">{spareAssetsItemLabel}</span>
            {asset.hasDetails && (
              <a href={getAssetViewUrl(asset)} className="assets-tree-popup__view-btn">
                {viewLabel}
              </a>
            )}
          </div>
        </div>
        {isExpanded && hasChildren && (
          <div className="assets-tree-popup__asset-children">
            {asset.assets.map(child => renderAssetRow(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  const renderAreaSection = (area: SiteAreaContainer, areaKey: string) => {
    const isExpanded = expandedAreas.has(areaKey)
    const areaLabel = [area.containerName, area.city, area.country].filter(Boolean).join(', ')
    return (
      <div key={areaKey} className="assets-tree-popup__area">
        <button type="button" className="assets-tree-popup__area-row" aria-expanded={isExpanded} onClick={() => toggleArea(areaKey)}>
          <span className="assets-tree-popup__area-chevron"><ChevronIcon open={isExpanded} /></span>
          <span className="assets-tree-popup__area-name">{areaLabel}</span>
          <span className="assets-tree-popup__type-label">{allAssetsSiteLabelValue}</span>
        </button>
        {isExpanded && (
          <div className="assets-tree-popup__area-assets">
            {area.assets.map(asset => renderAssetRow(asset))}
          </div>
        )}
      </div>
    )
  }

  type GroupConfig = { label: string | undefined; typeLabel: string | undefined; isExpanded: boolean; containers: SiteAreaContainer[]; onToggle: () => void }
  const renderGroup = ({ label, typeLabel, isExpanded, containers, onToggle }: GroupConfig) => (
    <div className="assets-tree-popup__group">
      <button type="button" className="assets-tree-popup__group-row" onClick={onToggle}>
        {containers.length > 0 && <span className="assets-tree-popup__area-chevron"><ChevronIcon open={isExpanded} /></span>}
        <span className="assets-tree-popup__group-name">{label}</span>
        <div className="assets-tree-popup__group-right">
          <span className="assets-tree-popup__asset-type">{typeLabel}</span>
        </div>
      </button>
      {isExpanded && containers.flatMap(c => c.assets).map(a => renderAssetRow(a))}
    </div>
  )

  const renderContent = () => {
    if (isLoading) return <div className="assets-tree-popup__loader"><Loader /></div>
    if (hasApiError) return <div className="assets-tree-popup__error"><p>{errorMessageLabel}</p></div>
    if (!treeData) return null

    const spare = treeData.spareAssetsContainers ?? []
    const decom = treeData.decommissionedAssetsContainers ?? []
    const flatAssets = (containers: SiteAreaContainer[]) => containers.flatMap(c => c.assets).map(a => renderAssetRow(a))

    const emptyMessage = (
      <div className="assets-tree-popup__empty-message">
        <p>{noAssetMessage || 'No Assets Found'}</p>
      </div>
    )

    if (selectedFilter === 'spare') return spare.length === 0 ? emptyMessage : <div className="assets-tree-popup__tree">{flatAssets(spare)}</div>
    if (selectedFilter === 'decom') return decom.length === 0 ? emptyMessage : <div className="assets-tree-popup__tree">{flatAssets(decom)}</div>

    const hasNoContent = (treeData.siteAreas ?? []).length === 0 &&
      (treeData.assets ?? []).length === 0 &&
      spare.length === 0 &&
      decom.length === 0

    return (
      <div className="assets-tree-popup__tree">
        {hasNoContent && emptyMessage}
        <button type="button" className="assets-tree-popup__site-row" aria-expanded={isSiteExpanded} onClick={() => setIsSiteExpanded(prev => !prev)}>
          <span className="assets-tree-popup__site-chevron"><ChevronIcon open={isSiteExpanded} /></span>
          <span className="assets-tree-popup__site-name">{treeData.siteName}</span>
          <span className="assets-tree-popup__type-label assets-tree-popup__type-label--red">{allAssetsSiteLabel}</span>
        </button>
        {isSiteExpanded && (
          <>
            {(treeData.siteAreas ?? []).map((area, i) => renderAreaSection(area, `area-${i}`))}
            {(treeData.assets ?? []).map(a => renderAssetRow(a))}
            <hr className="assets-tree-popup__groups-divider" />
            {renderGroup({ label: spareAssetsLabel, typeLabel: spareAssetsItemLabelValue, isExpanded: isSpareGroupExpanded, containers: spare, onToggle: () => { if (spare.length > 0) setIsSpareGroupExpanded(p => !p) } })}
            {renderGroup({ label: decommissionedAssetsLabel, typeLabel: decommissionedAssetsItemLabelValue, isExpanded: isDecomGroupExpanded, containers: decom, onToggle: () => { if (decom.length > 0) setIsDecomGroupExpanded(p => !p) } })}
          </>
        )}
      </div>
    )
  }

  return (
    <PopupContainer isOpen={isOpen} title="" subtitle="" onClose={onClose} closeOnOverlayClick hideCloseButton className="assets-tree-popup">
      <AssetsTreeHeader
        selectedFilter={selectedFilter}
        isDropdownOpen={isDropdownOpen}
        allAssetsLabel={allAssetsLabel}
        spareAssetsLabel={spareAssetsLabel}
        decommissionedAssetsLabel={decommissionedAssetsLabel}
        expandAllLabel={expandAllLabel}
        collapseAllLabel={collapseAllLabel}
        onToggleDropdown={() => setIsDropdownOpen(prev => !prev)}
        onFilterSelect={handleFilterSelect}
        onExpandAll={handleExpandAll}
        onCollapseAll={handleCollapseAll}
        onClose={onClose}
      />
      <div className="assets-tree-popup__content">{renderContent()}</div>
    </PopupContainer>
  )
}